import { useMemo, useState } from "react";
import { Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * CashFlowChart — grafik arus kas SVG native (2026-06-19 rework, user spec).
 * - Mode selector independen per grafik: "Per Periode" / "Kumulatif" (S-Curve).
 * - Pilihan mode persist di sessionStorage selama sesi halaman monitoring.
 * - Badge mode aktif (PER PERIODE / KUMULATIF).
 * - Tooltip titik: dipindah menjadi PANEL RINCIAN tetap di bawah grafik
 *   (2026-10-15, user report) — pop-up lama melayang di atas plot & menutupi
 *   garis saat kursor di titik atas/kanan. Panel in-flow TIDAK pernah menutupi
 *   grafik; tinggi dicadangkan (min-h) agar layout tidak melompat saat hover.
 * - Tombol layar penuh: grafik tampil besar dalam dialog untuk membaca detail.
 * - Uang Masuk hijau, Uang Keluar navy dalam satu chart.
 * - Bulan dilabel "Jan 2026"; minggu dilabel "2026-W11" (sudah dari backend).
 * Mode kumulatif dihitung running-sum dari series penuh sejak awal proyek —
 * baseline TIDAK pernah di-reset oleh filter tampilan.
 */
const MONTH_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtMonthKey = (k) => {
  const m = /^(\d{4})-(\d{2})$/.exec(k || "");
  return m ? `${MONTH_ID[Number(m[2]) - 1]} ${m[1]}` : k;
};
const fmtIDRShort = (v) => {
  const a = Math.abs(v);
  if (a >= 1e9) return `Rp${(v / 1e9).toFixed(1)}M`;
  if (a >= 1e6) return `Rp${(v / 1e6).toFixed(1)}Jt`;
  return `Rp${Math.round(v).toLocaleString("id-ID")}`;
};
const fmtIDRFull = (v) => `Rp ${Math.round(v).toLocaleString("id-ID")}`;
const fmtLabel = (k) => (/-W\d{2}$/.test(k) ? k : fmtMonthKey(k));

function ChartCanvas({ series, isCum, W, H, svgClass, testid }) {
  const Ph = 34, Pv = 26;
  const [hover, setHover] = useState(null);

  const valIn = (d) => (isCum ? d.cumIn : d.in) || 0;
  const valOut = (d) => (isCum ? d.cumOut : d.out) || 0;
  // 2026-10-14 (user request): seri "Progres" SELALU per periode — tidak ikut
  // mode kumulatif (progres fisik dilaporkan per periode, bukan running sum).
  const valProg = (d) => d.progress || 0;
  const maxVal = Math.max(1, ...series.flatMap((d) => [valIn(d), valOut(d), valProg(d)]));
  const n = series.length;
  // Jarak titik adaptif — label disampling kalau periode banyak.
  const xStep = (W - Ph * 2) / Math.max(1, n - 1);
  const yOf = (v) => H - Pv - (v / maxVal) * (H - Pv * 2);
  const path = (fn) => series.map((d, i) => `${i === 0 ? "M" : "L"}${Ph + i * xStep},${yOf(fn(d))}`).join(" ");
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxVal);
  const labelEvery = Math.max(1, Math.ceil(n / 8));

  const hov = hover != null ? series[hover] : null;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className={svgClass} onMouseLeave={() => setHover(null)}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={Ph} x2={W - Ph} y1={yOf(v)} y2={yOf(v)} stroke="#E2E8F0" strokeDasharray="2,3" />
            <text x={Ph - 4} y={yOf(v) + 3} textAnchor="end" fontSize="9" fill="#94A3B8">{fmtIDRShort(v)}</text>
          </g>
        ))}
        <path d={path(valIn)} fill="none" stroke="#059669" strokeWidth="2" />
        {/* 2026-06-20 (user request): Uang Keluar = MERAH agar kontras dgn Masuk (hijau). */}
        <path d={path(valOut)} fill="none" stroke="#DC2626" strokeWidth="2" />
        {/* 2026-10-14 (user request): garis ketiga "Progres" (nilai kontrak terealisasi fisik) — AMBER. */}
        <path d={path(valProg)} fill="none" stroke="#D97706" strokeWidth="2" strokeDasharray="6,3" data-testid={`${testid}-line-progress`} />
        {series.map((d, i) => (
          <g key={i}>
            {/* area hover tak kasat mata per titik */}
            <rect x={Ph + i * xStep - xStep / 2} y={0} width={Math.max(4, xStep)} height={H - Pv}
              fill="transparent" onMouseEnter={() => setHover(i)} data-testid={`${testid}-point-${i}`} />
            <circle cx={Ph + i * xStep} cy={yOf(valIn(d))} r={hover === i ? 4 : 2.5} fill="#059669" />
            <circle cx={Ph + i * xStep} cy={yOf(valOut(d))} r={hover === i ? 4 : 2.5} fill="#DC2626" />
            <circle cx={Ph + i * xStep} cy={yOf(valProg(d))} r={hover === i ? 4 : 2.5} fill="#D97706" />
            {i % labelEvery === 0 && (
              <text x={Ph + i * xStep} y={H - 6} textAnchor="middle" fontSize="8.5" fill="#64748B">{fmtLabel(d.key)}</text>
            )}
          </g>
        ))}
        {hover != null && (
          <line x1={Ph + hover * xStep} x2={Ph + hover * xStep} y1={4} y2={H - Pv} stroke="#94A3B8" strokeDasharray="3,3" />
        )}
      </svg>
      {/* 2026-10-15 (user report): pop-up keterangan periode dipindah ke panel
          rincian tetap DI BAWAH grafik — tidak lagi melayang menutupi garis. */}
      <div className="mt-1 border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[10px] min-h-[48px]" data-testid={`${testid}-tooltip`}>
        {hov ? (
          <>
            <div className="font-bold text-[#0B1D3A] text-[11px]">{fmtLabel(hov.key)}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
              <span className="text-slate-500">Masuk: <b className="tabular-nums text-emerald-700">{fmtIDRFull(hov.in || 0)}</b></span>
              <span className="text-slate-500">Keluar: <b className="tabular-nums text-[#DC2626]">{fmtIDRFull(hov.out || 0)}</b></span>
              <span className="text-slate-500" data-testid={`${testid}-tooltip-progress`}>Progres: <b className="tabular-nums text-[#D97706]">{fmtIDRFull(hov.progress || 0)}</b></span>
              <span className="text-slate-500">Kum. masuk: <b className="tabular-nums text-emerald-700">{fmtIDRFull(hov.cumIn)}</b></span>
              <span className="text-slate-500">Kum. keluar: <b className="tabular-nums text-[#DC2626]">{fmtIDRFull(hov.cumOut)}</b></span>
              <span className="text-slate-500">Posisi kas: <b className={`tabular-nums ${(hov.cumIn - hov.cumOut) < 0 ? "text-rose-700" : "text-[#0B1D3A]"}`}>{fmtIDRFull(hov.cumIn - hov.cumOut)}</b></span>
            </div>
          </>
        ) : (
          <div className="text-slate-400 pt-1.5">Arahkan kursor ke titik grafik untuk melihat rincian periode.</div>
        )}
      </div>
    </div>
  );
}

export default function CashFlowChart({ title, data, storageKey, testid }) {
  const ssKey = `ens_cf_mode_${storageKey || title}`;
  const [mode, setMode] = useState(() => {
    try { return sessionStorage.getItem(ssKey) || "periode"; } catch { return "periode"; }
  });
  const [fullOpen, setFullOpen] = useState(false);

  const changeMode = (m) => {
    setMode(m);
    try { sessionStorage.setItem(ssKey, m); } catch { /* noop */ }
  };

  // Series kumulatif — running sum dari periode pertama (baseline awal proyek).
  const series = useMemo(() => {
    const rows = (data || []).map((d) => ({ ...d }));
    let ci = 0, co = 0;
    return rows.map((d) => {
      ci += d.in || 0;
      co += d.out || 0;
      return { ...d, cumIn: ci, cumOut: co };
    });
  }, [data]);

  if (!series.length) return (
    <div className="bg-white border border-slate-200 p-4" data-testid={testid}>
      <h3 className="text-sm font-semibold text-[#0B1D3A]">{title}</h3>
      <div className="text-[11px] text-slate-400 mt-6 text-center">Belum ada data pergerakan kas.</div>
    </div>
  );

  const isCum = mode === "kumulatif";
  const badge = (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${isCum ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-emerald-600 text-white border-emerald-600"}`}
      data-testid={`${testid}-mode-badge`}>{isCum ? "Kumulatif" : "Per Periode"}</span>
  );
  const modeButtons = (prefix) => (
    <div className="inline-flex border border-slate-300" role="radiogroup" data-testid={`${prefix}-mode-selector`}>
      {[{ v: "periode", l: "Per Periode" }, { v: "kumulatif", l: "Kumulatif" }].map((m) => (
        <button key={m.v} type="button" onClick={() => changeMode(m.v)}
          data-testid={`${prefix}-mode-${m.v}`}
          className={`px-2 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors ${mode === m.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
          {m.l}
        </button>
      ))}
    </div>
  );
  const legend = (
    <div className="hidden sm:flex items-center gap-3 text-[10px] text-slate-600">
      <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-600" /> Masuk</span>
      <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-[#DC2626]" /> Keluar</span>
      <span className="inline-flex items-center gap-1" data-testid="cf-legend-progress"><span className="w-3 h-0.5 bg-[#D97706]" /> Progres</span>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#0B1D3A]">{title}</h3>
          {badge}
        </div>
        <div className="flex items-center gap-2">
          {modeButtons(testid)}
          {legend}
          <button type="button" onClick={() => setFullOpen(true)} title="Lihat layar penuh" aria-label={`${title} layar penuh`}
            data-testid={`${testid}-btn-fullscreen`}
            className="inline-flex items-center justify-center w-7 h-7 border border-slate-300 bg-white text-slate-600 hover:bg-[#0B1D3A] hover:text-white hover:border-[#0B1D3A] transition-colors">
            <Maximize2 size={13} />
          </button>
        </div>
      </div>
      <div className="text-[10px] text-slate-400 mb-1" data-testid={`${testid}-mode-hint`}>
        {isCum ? "Kumulatif: total sejak awal proyek sampai periode terpilih." : "Per Periode: nilai transaksi pada periode terpilih."}
      </div>

      <ChartCanvas series={series} isCum={isCum} W={560} H={220} svgClass="w-full h-44" testid={testid} />

      <Dialog open={fullOpen} onOpenChange={setFullOpen}>
        <DialogContent className="max-w-6xl w-[95vw]" data-testid={`${testid}-fullscreen-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0B1D3A]">
              {title}
              {badge}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {modeButtons(`${testid}-fs`)}
            {legend}
          </div>
          <div className="text-[10px] text-slate-400">
            {isCum ? "Kumulatif: total sejak awal proyek sampai periode terpilih." : "Per Periode: nilai transaksi pada periode terpilih."}
          </div>
          <ChartCanvas series={series} isCum={isCum} W={960} H={420} svgClass="w-full h-[55vh]" testid={`${testid}-fs`} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
